import * as React from "react";
import { useState, useEffect, useCallback, useRef, memo } from "react";
import { Check, FileText, ChevronDown, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/language-context";
import { useIsMobile } from "@/hooks/use-mobile";
import type { Framework } from "@/types/framework";
import type { LocaleType } from "@/types/chat";
import { useTranslation } from "react-i18next";

interface FrameworkSelectorProps {
  selectedFramework: Framework;
  onFrameworkChange: (framework: Framework) => void;
  disabled?: boolean;
  className?: string;
}

// Translations for auto-detect text
const autoDetectText: Record<LocaleType, string> = {
  en: "Auto Detect",
  ar: "تحديد تلقائي",
  zh: "自动检测",
  ja: "自動検出",
  ru: "Автоопределение",
  de: "Automatische Erkennung",
  fr: "Détection automatique",
  es: "Detección automática",
  hi: "स्वचालित पहचान",
  tr: "Otomatik Algılama",
  sv: "Automatisk identifiering"
};

// Auto-detect description text
const autoDetectDescription: Record<LocaleType, string> = {
  en: "AI will identify the most relevant law",
  ar: "سيحدد الذكاء الاصطناعي النظام الأكثر صلة",
  zh: "AI将识别最相关的法律",
  ja: "AIが最も関連性の高い法律を特定します",
  ru: "ИИ определит наиболее подходящий закон",
  de: "KI identifiziert das relevanteste Gesetz",
  fr: "L'IA identifiera la loi la plus pertinente",
  es: "La IA identificará la ley más relevante",
  hi: "AI सबसे प्रासंगिक कानून की पहचान करेगा",
  tr: "AI en alakalı yasayı belirleyecek",
  sv: "AI kommer att identifiera den mest relevanta lagen"
};

// Dialog title translations
const dialogTitle: Record<LocaleType, string> = {
  en: "Select Ministry of Commerce Law",
  ar: "اختر نظام وزارة التجارة",
  zh: "选择商务部法律",
  ja: "商務省法律を選択",
  ru: "Выберите закон Министерства торговли",
  de: "Gesetz des Handelsministeriums auswählen",
  fr: "Sélectionner une loi du Ministère du Commerce",
  es: "Seleccionar Ley del Ministerio de Comercio",
  hi: "वाणिज्य मंत्रालय का कानून चुनें",
  tr: "Ticaret Bakanlığı Kanununu Seçin",
  sv: "Välj handelsministeriets lag"
};

// Law categories with proper numbering
interface LawItem {
  id: string;
  framework: Framework;
  title: string;
  arabicTitle?: string;
}

interface CategoryItem {
  id: string;
  title: Record<LocaleType, string>;
  laws: LawItem[];
}

export default function FrameworkSelector({
  selectedFramework,
  onFrameworkChange,
  disabled = false,
  className = "",
}: FrameworkSelectorProps): JSX.Element {
  // State
  const [open, setOpen] = useState(false);
  
  // Hooks
  const { locale, isRTL } = useLanguage();
  const isMobile = useIsMobile();
  const selectedLawRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();

  // Categorized laws according to legal domains
  // Each law maintains its official legal name with category numbering
  const lawCategories: CategoryItem[] = [
    {
      id: "category-a",
      title: {
        en: "A. Business Formation and Identity",
        ar: "أ. تكوين الأعمال والهوية",
        zh: "A. 企业组建与身份",
        ja: "A. 企業形成とアイデンティティ",
        ru: "А. Формирование бизнеса и идентичность",
        de: "A. Unternehmensbildung und Identität",
        fr: "A. Formation et identité des entreprises",
        es: "A. Formación de negocios e identidad",
        hi: "A. व्यापार निर्माण और पहचान",
        tr: "A. İş Oluşumu ve Kimliği",
        sv: "A. Företagsbildning och identitet"
      },
      laws: [
        { id: "a1", title: "A1. Companies Law", arabicTitle: "أ1. نظام الشركات", framework: "MOC_COMPANIES_LAW" },
        { id: "a2", title: "A2. Professional Companies Law", arabicTitle: "أ2. نظام الشركات المهنية", framework: "MOC_PROFESSIONAL_COMPANIES_LAW" },
        { id: "a3", title: "A3. Commercial Register Law", arabicTitle: "أ3. نظام السجل التجاري", framework: "MOC_COMMERCIAL_REGISTER_LAW" },
        { id: "a4", title: "A4. Ministry of Commerce Law", arabicTitle: "أ4. نظام وزارة التجارة", framework: "MOC_MINISTRY_OF_COMMERCE_LAW" },
        { id: "a5", title: "A5. Trade Names Law", arabicTitle: "أ5. نظام الأسماء التجارية", framework: "MOC_TRADE_NAMES_LAW" },
        { id: "a6", title: "A6. GCC Trademark Law", arabicTitle: "أ6. نظام العلامات التجارية لدول مجلس التعاون الخليجي", framework: "MOC_GCC_TRADEMARK_LAW" }
      ]
    },
    {
      id: "category-b",
      title: {
        en: "B. Financial Instruments, Security, and Insolvency",
        ar: "ب. الأدوات المالية والأمن والإعسار",
        zh: "B. 金融工具、担保和破产",
        ja: "B. 金融商品、担保、倒産",
        ru: "Б. Финансовые инструменты, обеспечение и несостоятельность",
        de: "B. Finanzinstrumente, Sicherheit und Insolvenz",
        fr: "B. Instruments financiers, sécurité et insolvabilité",
        es: "B. Instrumentos financieros, garantía e insolvencia",
        hi: "B. वित्तीय उपकरण, सुरक्षा और दिवालियापन",
        tr: "B. Finansal Araçlar, Güvenlik ve İflas",
        sv: "B. Finansiella instrument, säkerhet och insolvens"
      },
      laws: [
        { id: "b1", title: "B1. Commercial Papers Law", arabicTitle: "ب1. نظام الأوراق التجارية", framework: "MOC_COMMERCIAL_PAPERS_LAW" },
        { id: "b2", title: "B2. Commercial Pledge Law", arabicTitle: "ب2. نظام الرهن التجاري", framework: "MOC_COMMERCIAL_PLEDGE_LAW" },
        { id: "b3", title: "B3. Movable Property Security Law", arabicTitle: "ب3. نظام ضمان الحقوق بالأموال المنقولة", framework: "MOC_MOVABLE_PROPERTY_SECURITY_LAW" },
        { id: "b4", title: "B4. Insolvency Law", arabicTitle: "ب4. نظام الإفلاس", framework: "MOC_INSOLVENCY_LAW" }
      ]
    },
    {
      id: "category-c",
      title: {
        en: "C. Commercial Relationships and Trade",
        ar: "ج. العلاقات التجارية والتجارة",
        zh: "C. 商业关系和贸易",
        ja: "C. 商業関係と貿易",
        ru: "В. Коммерческие отношения и торговля",
        de: "C. Handelsbeziehungen und Handel",
        fr: "C. Relations commerciales et commerce",
        es: "C. Relaciones comerciales y comercio",
        hi: "C. वाणिज्यिक संबंध और व्यापार",
        tr: "C. Ticari İlişkiler ve Ticaret",
        sv: "C. Kommersiella relationer och handel"
      },
      laws: [
        { id: "c1", title: "C1. Commercial Agencies Law", arabicTitle: "ج1. نظام الوكالات التجارية", framework: "MOC_COMMERCIAL_AGENCIES_LAW" },
        { id: "c2", title: "C2. Franchise Law", arabicTitle: "ج2. نظام الامتياز التجاري", framework: "MOC_FRANCHISE_LAW" },
        { id: "c3", title: "C3. E-Commerce Law", arabicTitle: "ج3. نظام التجارة الإلكترونية", framework: "MOC_ECOMMERCE_LAW" },
        { id: "c4", title: "C4. Chambers of Commerce Law", arabicTitle: "ج4. نظام الغرف التجارية", framework: "MOC_CHAMBERS_OF_COMMERCE_LAW" }
      ]
    },
    {
      id: "category-d",
      title: {
        en: "D. Commercial Records and Information",
        ar: "د. السجلات والمعلومات التجارية",
        zh: "D. 商业记录和信息",
        ja: "D. 商業記録と情報",
        ru: "Г. Коммерческие записи и информация",
        de: "D. Handelsunterlagen und Informationen",
        fr: "D. Registres commerciaux et informations",
        es: "D. Registros comerciales e información",
        hi: "D. वाणिज्यिक रिकॉर्ड और जानकारी",
        tr: "D. Ticari Kayıtlar ve Bilgiler",
        sv: "D. Handelsregister och information"
      },
      laws: [
        { id: "d1", title: "D1. Commercial Books Law", arabicTitle: "د1. نظام الدفاتر التجارية", framework: "MOC_COMMERCIAL_BOOKS_LAW" },
        { id: "d2", title: "D2. Commercial Data Law", arabicTitle: "د2. نظام البيانات التجارية", framework: "MOC_COMMERCIAL_DATA_LAW" },
        { id: "d3", title: "D3. Public Warehousing Law", arabicTitle: "د3. نظام المستودعات العامة", framework: "MOC_PUBLIC_WAREHOUSING_LAW" }
      ]
    },
    {
      id: "category-e",
      title: {
        en: "E. Standards, Quality, and Regulated Products",
        ar: "هـ. المعايير والجودة والمنتجات المنظمة",
        zh: "E. 标准、质量和受监管产品",
        ja: "E. 標準、品質および規制製品",
        ru: "Д. Стандарты, качество и регулируемые товары",
        de: "E. Standards, Qualität und regulierte Produkte",
        fr: "E. Normes, qualité et produits réglementés",
        es: "E. Estándares, calidad y productos regulados",
        hi: "E. मानक, गुणवत्ता, और विनियमित उत्पाद",
        tr: "E. Standartlar, Kalite ve Düzenlenmiş Ürünler",
        sv: "E. Standarder, kvalitet och reglerade produkter"
      },
      laws: [
        { id: "e1", title: "E1. Standards and Metrology Law", arabicTitle: "هـ1. نظام الهيئة السعودية للمواصفات والمقاييس والجودة", framework: "MOC_STANDARDS_METROLOGY_LAW" },
        { id: "e2", title: "E2. Calibration and Measurement Law", arabicTitle: "هـ2. نظام القياس والمعايرة", framework: "MOC_CALIBRATION_MEASUREMENT_LAW" },
        { id: "e3", title: "E3. Private Laboratories Law", arabicTitle: "هـ3. نظام المختبرات الخاصة", framework: "MOC_PRIVATE_LABORATORIES_LAW" },
        { id: "e4", title: "E4. Precious Metals and Gemstones Law", arabicTitle: "هـ4. نظام المعادن الثمينة والأحجار الكريمة", framework: "MOC_PRECIOUS_METALS_GEMSTONES_LAW" },
        { id: "e5", title: "E5. Chemicals Import Management Law", arabicTitle: "هـ5. نظام إدارة استيراد المواد الكيميائية", framework: "MOC_CHEMICALS_IMPORT_MANAGEMENT_LAW" }
      ]
    },
    {
      id: "category-f",
      title: {
        en: "F. Professional Regulation and Oversight",
        ar: "و. التنظيم المهني والإشراف",
        zh: "F. 专业监管和监督",
        ja: "F. 専門職規制と監督",
        ru: "Е. Профессиональное регулирование и надзор",
        de: "F. Berufsregulierung und Aufsicht",
        fr: "F. Réglementation professionnelle et surveillance",
        es: "F. Regulación profesional y supervisión",
        hi: "F. पेशेवर विनियमन और निरीक्षण",
        tr: "F. Mesleki Düzenleme ve Denetim",
        sv: "F. Yrkesreglering och tillsyn"
      },
      laws: [
        { id: "f1", title: "F1. Accounting and Auditing Law", arabicTitle: "و1. نظام المحاسبين القانونيين", framework: "MOC_ACCOUNTING_AUDITING_LAW" },
        { id: "f2", title: "F2. Valuers Law", arabicTitle: "و2. نظام المقيمين المعتمدين", framework: "MOC_VALUERS_LAW" },
        { id: "f3", title: "F3. Engineers Law", arabicTitle: "و3. نظام مجلس المهندسين السعوديين", framework: "MOC_ENGINEERS_LAW" }
      ]
    },
    {
      id: "category-g",
      title: {
        en: "G. Compliance and Dispute Resolution",
        ar: "ز. الامتثال وحل النزاعات",
        zh: "G. 合规与争议解决",
        ja: "G. コンプライアンスと紛争解決",
        ru: "Ж. Соблюдение норм и разрешение споров",
        de: "G. Compliance und Streitbeilegung",
        fr: "G. Conformité et résolution des litiges",
        es: "G. Cumplimiento y resolución de disputas",
        hi: "G. अनुपालन और विवाद समाधान",
        tr: "G. Uyum ve Anlaşmazlık Çözümü",
        sv: "G. Efterlevnad och tvistlösning"
      },
      laws: [
        { id: "g1", title: "G1. Anti-Concealment Law", arabicTitle: "ز1. نظام مكافحة التستر", framework: "MOC_ANTI_CONCEALMENT_LAW" },
        { id: "g2", title: "G2. Anti-Money Laundering Law", arabicTitle: "ز2. نظام مكافحة غسل الأموال", framework: "MOC_ANTI_MONEY_LAUNDERING_LAW" },
        { id: "g3", title: "G3. Arbitration Law", arabicTitle: "ز3. نظام التحكيم", framework: "MOC_ARBITRATION_LAW" },
        { id: "g4", title: "G4. Combating Commercial Adulteration Law", arabicTitle: "ز4. نظام مكافحة الغش التجاري", framework: "MOC_COMBATING_COMMERCIAL_ADULTERATION_LAW" },
        { id: "g5", title: "G5. GCC Anti-Dumping Law", arabicTitle: "ز5. نظام مكافحة الإغراق لدول مجلس التعاون الخليجي", framework: "MOC_GCC_COMBATING_DUMPING_LAW" }
      ]
    }
  ];

  // Utility function to get localized framework display name
  const getFrameworkDisplayName = (framework: Framework): string => {
    // Special case for AUTO
    if (framework === "AUTO") {
      return autoDetectText[locale] || autoDetectText.en;
    }
    
    // For Arabic locale, use Arabic titles
    if (locale === 'ar') {
      for (const category of lawCategories) {
        const foundLaw = category.laws.find(law => law.framework === framework);
        if (foundLaw?.arabicTitle) {
          return foundLaw.arabicTitle;
        }
      }
    }
    
    // For other languages, use the English title or i18n
    for (const category of lawCategories) {
      const foundLaw = category.laws.find(law => law.framework === framework);
      if (foundLaw?.title) {
        // Check i18n first
        const translated = t(framework);
        if (translated !== framework) {
          return translated;
        }
        return foundLaw.title;
      }
    }
    
    // Fallback for frameworks not in our categories
    const name = framework.replace('MOC_', '').replace(/_/g, ' ').replace(/LAW$/, 'Law');
    return name.replace(/\w+/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  };

  // Event handler to change the framework
  const handleFrameworkChange = useCallback((framework: Framework) => {
    onFrameworkChange(framework);
    setOpen(false);
  }, [onFrameworkChange]);
  
  // For mobile browsers - calculate viewport height
  useEffect(() => {
    const setVh = () => {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    };
    
    setVh();
    window.addEventListener('resize', setVh);
    return () => window.removeEventListener('resize', setVh);
  }, []);

  // Reference for scrollable content
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Scroll to selected item when dialog opens with smooth animation
  useEffect(() => {
    if (open) {
      // Give the dialog time to fully render
      const timer = setTimeout(() => {
        const selectedItem = selectedLawRef.current;
        const container = scrollContainerRef.current;
        
        if (selectedItem && container) {
          // Calculate scroll position to center the item
          const itemOffset = selectedItem.offsetTop;
          const containerHeight = container.clientHeight;
          const itemHeight = selectedItem.clientHeight;
          
          const scrollPosition = itemOffset - (containerHeight / 2) + (itemHeight / 2);
          
          // Apply smooth scroll animation
          container.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
          
          console.log("[FrameworkSelector] Smooth scrolled to:", selectedFramework);
        }
      }, 250);
      
      return () => clearTimeout(timer);
    }
  }, [open, selectedFramework]);

  // Item Components
  const ListItem = memo(({ 
    framework,
    isSelected,
    showSubtitle = false,
    subtitle = "",
    onClick
  }: { 
    framework: Framework, 
    isSelected: boolean,
    showSubtitle?: boolean,
    subtitle?: string,
    onClick: () => void 
  }) => {
    const isAuto = framework === "AUTO";
    const icon = isAuto ? <Search size={16} /> : <FileText size={16} />;
    const title = getFrameworkDisplayName(framework);

    return (
      <button
        ref={isSelected ? selectedLawRef : undefined}
        type="button"
        role="option"
        aria-selected={isSelected}
        onClick={onClick}
        className={cn(
          "w-full text-sm rounded-md transition-colors select-none",
          "px-3.5 py-2.5",
          showSubtitle ? "mb-3" : "mb-1.5",
          isSelected ? "bg-primary/10 text-primary" : "hover:bg-muted"
        )}
      >
        <div className="flex items-center justify-between gap-2" 
             dir={isRTL ? "rtl" : "ltr"}>
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Title with Icon */}
            <div className="flex items-center gap-2.5">
              {isRTL ? (
                // RTL layout - icon should be on the right (start) in RTL
                <>
                  <div className={cn(
                    "flex-shrink-0 rounded-full w-6 h-6 flex items-center justify-center",
                    isSelected ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                  )}>
                    {icon}
                  </div>
                  <span className={cn("truncate", isSelected && "font-medium")}>{title}</span>
                </>
              ) : (
                // LTR layout - icon on the left (start)
                <>
                  <div className={cn(
                    "flex-shrink-0 rounded-full w-6 h-6 flex items-center justify-center",
                    isSelected ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                  )}>
                    {icon}
                  </div>
                  <span className={cn("truncate", isSelected && "font-medium")}>{title}</span>
                </>
              )}
            </div>
            
            {/* Optional Subtitle */}
            {showSubtitle && subtitle && (
              <div className="mt-0.5 text-xs text-muted-foreground">
                {subtitle}
              </div>
            )}
          </div>
          
          {/* Selected Indicator */}
          {isSelected && (
            <Check size={16} className="flex-shrink-0 text-primary" />
          )}
        </div>
      </button>
    );
  });
  
  ListItem.displayName = "ListItem";

  // Category Component
  const Category = memo(({ 
    category 
  }: { 
    category: CategoryItem 
  }) => {
    const categoryTitle = category.title[locale] || category.title.en;
    
    return (
      <div className="mb-3">
        {/* Category Header - Clean Style */}
        <div 
          className={cn(
            "px-3.5 py-2",
            "text-sm font-semibold sticky top-0 z-10 select-none",
            "bg-background",
            "border-l-2 border-primary/40 border-b border-border/40"
          )}
          dir={isRTL ? "rtl" : "ltr"}
        >
          {categoryTitle}
        </div>
        
        {/* Category Items */}
        <div className="pt-1.5">
          {category.laws.map((law) => (
            <ListItem
              key={law.framework}
              framework={law.framework}
              isSelected={selectedFramework === law.framework}
              onClick={() => handleFrameworkChange(law.framework)}
            />
          ))}
        </div>
      </div>
    );
  });
  
  Category.displayName = "Category";

  // Main Dialog Content
  const selectorButton = (
    <Button 
      variant="outline" 
      className={cn("w-full h-10", className)}
      disabled={disabled}
    >
      <div className="flex items-center justify-between w-full" dir={isRTL ? "rtl" : "ltr"}>
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {isRTL ? (
            // RTL layout - icon should be on the right (start) in RTL
            <>
              {selectedFramework === "AUTO" 
                ? <Search size={16} aria-hidden="true" />
                : <FileText size={16} aria-hidden="true" />
              }
              <span className="font-medium truncate">
                {selectedFramework === "AUTO" 
                  ? autoDetectText[locale] || autoDetectText.en
                  : getFrameworkDisplayName(selectedFramework)}
              </span>
            </>
          ) : (
            // LTR layout - icon on the left (start)
            <>
              {selectedFramework === "AUTO" 
                ? <Search size={16} aria-hidden="true" />
                : <FileText size={16} aria-hidden="true" />
              }
              <span className="font-medium truncate">
                {selectedFramework === "AUTO" 
                  ? autoDetectText[locale] || autoDetectText.en
                  : getFrameworkDisplayName(selectedFramework)}
              </span>
            </>
          )}
        </div>
        <ChevronDown size={16} className={cn("opacity-50", isRTL ? "mr-2" : "ml-2")} />
      </div>
    </Button>
  );
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {selectorButton}
      </DialogTrigger>

      <DialogContent 
        className={cn(
          "p-0 overflow-hidden rounded-xl select-none",
          isMobile 
            ? "w-[calc(100%-32px)] max-w-[420px] h-[520px] max-h-[80vh]" 
            : "max-w-[420px] h-[520px] max-h-[90vh]"
        )}
        style={{
          display: "flex",
          flexDirection: "column"
        }}
      >
        {/* Dialog Header */}
        <div className="flex-shrink-0 border-b bg-muted/20">
          <DialogTitle className="py-3.5 px-4 text-lg font-medium text-center select-none" dir={isRTL ? "rtl" : "ltr"}>
            {dialogTitle[locale] || dialogTitle.en}
          </DialogTitle>
        </div>
        
        {/* Scrollable Content */}
        <div 
          ref={scrollContainerRef}
          className="flex-grow overflow-y-auto overscroll-contain px-4 py-5"
          dir={isRTL ? "rtl" : "ltr"}
        >
          <div role="listbox" aria-label={dialogTitle[locale]} className="space-y-3">
            {/* Auto Detect Option - Clean Styling */}
            <div className="mb-5 border-l-2 border-primary/40 pl-1">
              <ListItem
                framework="AUTO"
                isSelected={selectedFramework === "AUTO"}
                onClick={() => handleFrameworkChange("AUTO")}
                showSubtitle={true}
                subtitle={autoDetectDescription[locale] || autoDetectDescription.en}
              />
            </div>
            
            {/* Categories */}
            {lawCategories.map(category => (
              <Category key={category.id} category={category} />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}